import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Sparkles, Loader2, X, Save, RotateCcw, CheckCircle, AlertCircle, ClipboardEdit
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

/**
 * CoachSummaryEditor — modal for generating, editing, and saving the
 * coach-reviewed AI draft used by the Athlete Dossier.
 *
 * Flow:
 *  1. Generate Draft (Claude)  →  editable text area
 *  2. Coach edits / adds comments
 *  3. Save (draft) or Approve & Save (final)
 *  4. Closes modal — caller decides whether to proceed to dossier generation
 */
export default function CoachSummaryEditor({ childId, childName, open, onClose, onSaved }) {
  const [summary, setSummary] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && childId) {
      fetchLatest();
    }
  }, [open, childId]);

  const fetchLatest = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/dossier/${childId}/coach-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.exists) {
        setSummary(res.data);
        setText(res.data.recommendation_text || '');
      } else {
        setSummary(null);
        setText('');
      }
    } catch (err) {
      console.error('Failed to fetch coach summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateDraft = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      toast.loading('Generating draft from Claude (~20s)…', { id: 'gen-summary', duration: 60000 });
      const res = await axios.post(
        `${API}/dossier/${childId}/coach-summary/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.dismiss('gen-summary');
      setSummary(res.data);
      setText(res.data.recommendation_text || '');
      toast.success('Draft generated. Review and edit before saving.');
    } catch (err) {
      toast.dismiss('gen-summary');
      console.error('Generate draft error:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate draft');
    } finally {
      setGenerating(false);
    }
  };

  const saveSummary = async (status) => {
    if (!summary?.id) {
      toast.error('Generate a draft first');
      return;
    }
    if (!text.trim()) {
      toast.error('Coach Summary cannot be empty');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/dossier/${childId}/coach-summary/${summary.id}`,
        { text: text.trim(), status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummary(res.data);
      toast.success(status === 'approved' ? 'Approved and saved.' : 'Saved as draft.');
      if (onSaved) onSaved(res.data);
      if (status === 'approved') {
        onClose();
      }
    } catch (err) {
      console.error('Save summary error:', err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const statusBadge = summary?.status === 'approved'
    ? { label: 'APPROVED', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' }
    : summary?.edited_by_coach
      ? { label: 'COACH EDITED · DRAFT', color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' }
      : summary
        ? { label: 'AI DRAFT', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' }
        : { label: 'NOT GENERATED', color: 'bg-zinc-700 text-zinc-400 border-zinc-600' };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="coach-summary-modal"
    >
      <div
        className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-3xl max-h-[95vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-zinc-800 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-emerald-500/20 rounded-xl flex items-center justify-center shrink-0">
              <ClipboardEdit className="h-5 w-5 text-[#02b5ff]" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-white truncate">Coach Summary</h2>
              <p className="text-xs text-zinc-400 truncate">
                {childName} — AI-drafted, coach-reviewed insights for the Athlete Dossier
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white p-1 shrink-0"
            data-testid="close-coach-summary-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Status + actions row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:px-6 sm:py-3 border-b border-zinc-800 bg-zinc-950/40 shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2.5 py-1 text-[10px] font-bold tracking-wider rounded border ${statusBadge.color}`} data-testid="coach-summary-status">
              {statusBadge.label}
            </span>
            {summary?.updated_at && (
              <span className="text-xs text-zinc-500">
                Updated {new Date(summary.updated_at).toLocaleString()}
              </span>
            )}
          </div>
          <Button
            onClick={generateDraft}
            disabled={generating || loading}
            variant="ghost"
            className="bg-[#02b5ff]/10 hover:bg-[#02b5ff]/20 text-[#02b5ff] border border-[#02b5ff]/30 text-sm h-9"
            data-testid="generate-coach-summary-btn"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : summary ? (
              <RotateCcw className="h-4 w-4 mr-2" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            {summary ? 'Re-generate' : 'Generate Draft'}
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
            </div>
          ) : !summary && !text ? (
            <div className="text-center py-12">
              <Sparkles className="h-12 w-12 mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400 mb-2 font-semibold">No Coach Summary yet</p>
              <p className="text-zinc-500 text-sm mb-6 max-w-md mx-auto">
                Click <strong>Generate Draft</strong> above to have Claude analyse {childName}'s
                latest data. You'll be able to edit the draft before it goes into the dossier.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400">
                Editable summary <span className="text-zinc-600">· Markdown supported</span>
              </label>
              <Textarea
                value={text}
                onChange={e => setText(e.target.value)}
                rows={20}
                className="font-mono text-sm leading-relaxed bg-zinc-950 border-zinc-800 text-zinc-100 focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] min-h-[400px] resize-y"
                placeholder="## Performance Analysis&#10;- ...&#10;&#10;## Priority Training Areas&#10;### Priority 1: ..."
                data-testid="coach-summary-textarea"
              />
              <div className="flex items-start gap-2 p-3 rounded bg-amber-500/5 border border-amber-500/20">
                <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-200/80">
                  Use <code className="bg-zinc-800 px-1 rounded">##</code> for sections,
                  <code className="bg-zinc-800 px-1 rounded mx-1">###</code> for sub-headings,
                  <code className="bg-zinc-800 px-1 rounded">**bold**</code>,
                  and <code className="bg-zinc-800 px-1 rounded">- item</code> for bullets.
                  Avoid lines of asterisks (***) — they print literally.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-2 p-4 sm:p-6 border-t border-zinc-800 shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-zinc-700 text-zinc-300"
            data-testid="cancel-coach-summary-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={() => saveSummary('draft')}
            disabled={saving || !text.trim() || !summary?.id}
            variant="outline"
            className="flex-1 border-[#02b5ff]/40 text-[#02b5ff] hover:bg-[#02b5ff]/10"
            data-testid="save-coach-summary-draft-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Draft
          </Button>
          <Button
            onClick={() => saveSummary('approved')}
            disabled={saving || !text.trim() || !summary?.id}
            className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white"
            data-testid="approve-coach-summary-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Approve & Save
          </Button>
        </div>
      </div>
    </div>
  );
}
